<script setup lang="ts">
import Notifications from '@/features/Notifications/index';
import {
  ROOT_LAWYERS_PAGE_URL,
  ROOT_CATALOG_URL,
  ROOT_JOURNAL_URL,
} from '@/shared/constants/urlConstants';
import { LAWYER_TYPE_LAWYER } from '@/shared/constants/lawyerTypeConstants';

import {
  ROLE_TYPE_COMPANY,
  ROLE_TYPE_LAWYER,
  ROLE_TYPE_USER,
} from '@/shared/constants/roleTypeConstants';

import { useModalStore } from '@/shared/ui/AppModal/store/modal';
import { useUserStore } from '@/entities/user/store';

defineOptions({
  name: 'AppHeader',
});

const modalStore = useModalStore();
const userStore = useUserStore();
const router = useRouter();
const route = useRoute();

const isShowNotification: Ref<boolean> = ref(false);

const { user, isAuth } = storeToRefs(userStore);
const userRoleType: ComputedRef<number> = computed(() => Number(user.value?.role_type));
const isHasCompanyId: ComputedRef<boolean> = computed(
  () => !!user.value?.lawyer?.company_id,
);
const isLawyerCabinet: ComputedRef<boolean> = computed(
  () =>
    Number(user.value?.lawyer?.moderation?.role) === LAWYER_TYPE_LAWYER ||
    Number(user.value?.lawyer?.role) === LAWYER_TYPE_LAWYER,
);

const hasNotification: ComputedRef<boolean> = computed(() =>
  Boolean(user.value?.hasNotification),
);

const isCabinetPage: ComputedRef<boolean> = computed(() =>
  route.path.startsWith('/cabinet'),
);

const accountTypeText: ComputedRef<string> = computed(() => {
  if (!isAuth.value) {
    return 'Вход и регистрация';
  }
  if (isAuth.value && userRoleType.value === ROLE_TYPE_USER) {
    return 'Кабинет клиента';
  }
  if (isAuth.value && userRoleType.value === ROLE_TYPE_LAWYER) {
    return isLawyerCabinet.value ? 'Кабинет юриста' : 'Кабинет адвоката';
  }
  if (isAuth.value && userRoleType.value === ROLE_TYPE_COMPANY) {
    return 'Кабинет организации';
  }
  return '';
});

const accountIcon: ComputedRef<string> = computed(() =>
  !isAuth.value || (isAuth.value && !isHasCompanyId.value)
    ? 'PersonProfile'
    : 'PeopleProfile',
);

const showAuthModal = (isLogin = true): void => {
  if (!isAuth.value) {
    modalStore.setModalState('auth', true, {
      isLogin,
      mode: ROLE_TYPE_USER,
    });
  } else {
    router.push({ path: '/cabinet/profile/' });
  }
};

const toggleNotificationModal = (): void => {
  isShowNotification.value = !isShowNotification.value;
};

const hideNotifications = (): void => {
  isShowNotification.value = false;
};

watch(() => route.fullPath, hideNotifications);
</script>

<template>
  <header class="header">
    <div class="header__inner">
      <AppLogo />
      <nav class="header__nav">
        <ul class="header__nav-list">
          <li class="header__nav-item">
            <NuxtLink
              :to="ROOT_CATALOG_URL"
              class="header__nav-link"
              active-class="_active"
            >
              Каталог юристов
            </NuxtLink>
          </li>
          <li class="header__nav-item">
            <NuxtLink
              :to="ROOT_JOURNAL_URL"
              class="header__nav-link"
              active-class="_active"
              exact-active-class="_active"
            >
              Журнал
            </NuxtLink>
          </li>
        </ul>
      </nav>
      <ul class="header__actions-list">
        <li class="header__actions-item">
          <NuxtLink
            class="header__action-link _lawyer"
            :to="ROOT_LAWYERS_PAGE_URL"
            active-class="_active"
            exact-active-class="_active"
          >
            <AppIcon class="header__action-icon _lawyer" name="Case" />
            <span class="header__action-text">Вы юрист?</span>
          </NuxtLink>
        </li>
        <li class="header__actions-item">
          <button
            :class="['header__action-button', { _active: isCabinetPage }]"
            @click.prevent="showAuthModal(true)"
          >
            <AppIcon
              :key="accountIcon"
              :class="[
                'header__action-icon',
                { _user: !isHasCompanyId },
                { _company: isHasCompanyId },
              ]"
              :name="accountIcon"
            />
            <span class="header__action-text">{{ accountTypeText }}</span>
          </button>
        </li>
        <li
          v-if="isAuth"
          v-click-outside="hideNotifications"
          class="header__actions-item"
        >
          <button
            :class="[
              'header__action-button',
              '_notification',
              { _open: isShowNotification },
            ]"
            aria-label="Уведомления"
            data-testid="header-notification-button"
            @click.prevent="toggleNotificationModal"
          >
            <span
              :class="['header__action-icon', { _notification: hasNotification }]"
              data-testid="header-notification-icon"
            >
              <AppIcon name="Bell" />
            </span>
          </button>
          <div v-if="isShowNotification" class="header__notification-modal">
            <Notifications />
          </div>
        </li>
      </ul>
    </div>
  </header>
</template>

<style lang="less">
.header {
  padding: @size-x5 0;
  background-color: white;

  &__nav {
    font-size: 14px;
    line-height: 130%;
    letter-spacing: -0.3px;
    z-index: 6;
    margin-left: 75px;
  }

  &__nav-list {
    display: flex;
    align-items: center;
    gap: @size-x4;
  }

  &__nav-link {
    .underline-hover-after();

    display: block;
    font-weight: 600;
    color: @color-cornflower-blue;
    min-height: 22px;
    padding: 0 2px 2px;
    position: relative;
    white-space: nowrap;

    &._active {
      color: @color-elephant;

      &::after {
        position: absolute;
        bottom: -2px;
        left: 0;
        width: 100%;
        height: 2px;
        background-color: @color-persimmon;
      }
    }
  }

  &__nav-button {
    display: flex;
    align-items: center;
    gap: 5px;
    font-weight: 600;
    color: @color-cornflower-blue;
    height: 22px;
    padding: 0 2px 2px;
    position: relative;
  }

  &__inner {
    .container-d();

    display: flex;
    align-items: center;
    position: relative;
  }

  &__actions-list {
    display: flex;
    gap: @size-x5;
    align-items: center;
    z-index: 6;
    margin-left: auto;
  }

  &__action-button {
    &:not(._notification) {
      .underline-hover-after();
    }

    display: flex;
    font-weight: 600;
    color: @color-cornflower-blue;
    height: 22px;
    padding-bottom: 2px;
    position: relative;

    &._active {
      color: @color-elephant;

      &::after {
        position: absolute;
        bottom: -2px;
        left: 0;
        width: 100%;
        height: 2px;
        background-color: @color-persimmon;
      }
    }
  }

  &__action-link {
    .underline-hover-after();

    display: flex;
    font-weight: 600;
    color: @color-cornflower-blue;
    height: 22px;
    padding-bottom: 2px;
    position: relative;

    &._active {
      color: @color-elephant;

      &::after {
        position: absolute;
        bottom: -2px;
        left: 0;
        width: 100%;
        height: 2px;
        background-color: @color-persimmon;
      }
    }
  }

  &__action-icon {
    display: inline-flex;
    width: @size-x4;
    height: @size-x4;
    margin-right: 5px;

    &._lawyer path {
      stroke: @color-persimmon;
    }

    &._user path {
      fill: @color-persimmon;
    }

    &._company {
      width: @size-x6;
      height: @size-x4;

      path {
        stroke: @color-persimmon;
        fill: transparent;
      }
    }

    &._notification {
      position: relative;

      &::after {
        content: '';
        position: absolute;
        top: -3px;
        right: 0;
        width: @size-x2;
        height: @size-x2;
        border-radius: 50%;
        background: @color-crimson;
      }
    }
  }

  &__action-button._notification &__action-icon svg .bell-fill {
    fill: transparent;
    transition: fill @A;
  }

  &__action-button._notification:hover &__action-icon svg .bell-fill,
  &__action-button._notification._open &__action-icon svg .bell-fill {
    fill: @color-persimmon;
  }

  &__action-text {
    font-size: 14px;
    line-height: 19px;
    letter-spacing: -0.3px;

    @media @tablet {
      font-size: 13px;
      line-height: 18px;
    }
  }

  &__notification-modal {
    width: 650px;
    position: absolute;
    top: @size-x10;
    right: 0;
    z-index: 1000;
  }
}
</style>
